import { IDriver } from "@interfaces/IDriver";
import { IGeofence } from "@interfaces/IGeofences";
import { IVehicle } from "@interfaces/IVehicle";
import { IVehicleDriverMapping } from "@interfaces/IVehicleDriverMapping";
import { CacheService } from "@services/cache.Service";
import { AccountOption } from "@services/class/AccountOption";
import { DriverService } from "@services/driver.service";
import { DriverAssignmentService } from "@services/driverAssignment.service";
import { GeofenceService } from "@services/geofence.service";
import { GoogleService } from "@services/google.service";
import { NavigationService } from "@services/navigation.service";
import { PlotService } from "@services/plot.service";
import { SessionService } from "@services/session.service";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import { VeriDate } from "@services/veriDate.service";
import { RouteStatus } from "@tms/_enums/RouteStatus";
import { RouteVisitStatus } from "@tms/_enums/RouteVisitStatus";
import { IGeoArea } from "@tms/_models/IGeoArea";
import { IImportedPlannedRoute, IPlannedRoute, VisitType } from "@tms/_models/IPlannedRoute";
import { IPlannedRouteVisit } from "@tms/_models/IPlannedRouteVisit";
import { IRouteDetailVisit } from "../_models/IRouteDetailVisit";
import { IUnplannedStop } from "@tms/_models/IUnplannedStop";
import { PlannedRouteVisitService } from "@tms/_services/planned-route-visit.service";
import { UnplannedStopService } from "@tms/_services/unplanned-stop.service";
import * as moment from "moment";
import { SettingsName } from "src/app/class/SettingsName";
import { ITmsReplayResult } from "@components/planning/interfaces/ITmsReplayResult";
import { GeofenceType } from "@enums/GeofenceType";
import { IPlannedRouteOptions } from "../_models/IPlannedRouteOptions";
import { DateService, DateUnit } from "@app/services/date.service";
import { VisitOnTimeStatus } from "../_enums/VisitOnTimeStatus";
import { PlannedRouteClosureReason } from "../_enums/PlannedRouteClosureReason";

export function selectMany<TIn, TOut>(input: TIn[], selectListFn: (t: TIn) => TOut[]): TOut[] {
    return input.reduce((out, inx) => {
        out.push(...selectListFn(inx));
        return out;
    }, new Array<TOut>());
}

export interface IPlannedRouteDetail extends IPlannedRoute {
    editingDisabled?: boolean;
    deletingDisabled?: boolean;

    unplannedVisits?: IUnplannedStop[];
    allVisits?: IRouteDetailVisit[];

    vehicle?: IVehicle;
    driver?: IDriver;
    totalDurationMins?: number;
    totalPlannedDistanceKm?: number;
    totalActualDistanceKm?: number;
    routeDisplayStatus?: string;
    routeDetailDisplayStatus?: string;
    firstVisit?: IPlannedRouteVisit;
    lastVisit?: IPlannedRouteVisit;
    routeDisplayName?: string;
    totalPlannedVisits?: number;

    plannedDuration?: number;
    actualDuration?: number;
    plannedRouteClosureReason?: PlannedRouteClosureReason;
}

export class PlannedRouteService {

    geofences: IGeofence[];

    plannedRouteServiceCache = "plannedRouteServiceCache";

    static $inject = ["$http", "$q", "cacheService", "driverService", "driverAssignmentService", "geofenceService", "googleService", "plotService", "translationService", "vehicleService", "navigationService", "veriDate", "sessionService", "plannedRouteVisitService", "unplannedStopService", "dateService"];

    constructor(
        private readonly $http: angular.IHttpService,
        private readonly $q: angular.IQService,
        private readonly cacheService: CacheService,
        private readonly driverService: DriverService,
        private readonly driverAssignmentService: DriverAssignmentService,
        private readonly geofenceService: GeofenceService,
        private readonly googleService: GoogleService,
        private readonly plotService: PlotService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: VehicleService,
        private readonly navigationService: NavigationService,
        private readonly veriDate: VeriDate,
        private readonly sessionService: SessionService,
        private readonly plannedRouteVisitService: PlannedRouteVisitService,
        private readonly unplannedStopService: UnplannedStopService,
        private readonly dateService: DateService
    ) {
        this.cacheService.createCache(this.plannedRouteServiceCache, { maxAge: 5 * 60 * 1000 });
    }

    replayRoute(plannedRouteId: number, updateDatabase: boolean): angular.IPromise<ITmsReplayResult> {
        return this
            .$http({
                url: `/api/tmsreplay/`,
                method: "POST",
                params: { updateDatabase: updateDatabase, routeId: plannedRouteId }
            })
            .then((response: angular.IHttpPromiseCallbackArg<ITmsReplayResult>) => {

                return response.data;
            });
    }

    cancelPlannedRoute(plannedRoute: IPlannedRoute): angular.IPromise<IPlannedRoute> {
        plannedRoute.Status = RouteStatus.Cancelled;

        this.removeDetailPropertiesForSave(plannedRoute);

        return this
            .$http({
                url: `/api/tms/plannedroute/cancel`,
                method: "POST",
                data: plannedRoute
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPlannedRoute>) => {
                this.cacheService.addOrUpdateItem(this.plannedRouteServiceCache, plannedRoute, (route1, route2) => route1.Id == route2.Id);
                return response.data;
            });
    }

    updatePlannedRoute(plannedRoute: IPlannedRoute): angular.IPromise<IPlannedRoute> {
        this.removeDetailPropertiesForSave(plannedRoute);

        return this
            .$http({
                url: `/api/tms/routes`,
                method: "POST",
                data: plannedRoute
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPlannedRoute>) => {
                this.cacheService.addOrUpdateItem(this.plannedRouteServiceCache, plannedRoute, (route1, route2) => route1.Id == route2.Id);
                return response.data;
            });
    }

    addPlannedRoute(route: IPlannedRoute): angular.IPromise<IPlannedRoute> {

        return this
            .$http({
                url: `/api/routeimport/addroute`,
                method: "POST",
                data: route
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPlannedRoute>) => {
                this.cacheService.addOrUpdateItem(this.plannedRouteServiceCache, route, (route1, route2) => route1.Id == route2.Id);
                return response.data;
            });
    }

    addPlannedRoutes(routes: IPlannedRoute[]): angular.IPromise<IImportedPlannedRoute[]> {

        return this
            .$http({
                url: `/api/routeimport/addroutes`,
                method: "POST",
                data: routes
            })
            .then((response: angular.IHttpPromiseCallbackArg<IImportedPlannedRoute[]>) => {
                routes.forEach(route => {
                    this.cacheService.addOrUpdateItem(this.plannedRouteServiceCache, route, (route1, route2) => route1.Id == route2.Id);
                })
                return response.data;
            });
    }

    getPlannedRoute(routeId: number, options: IPlannedRouteOptions): angular.IPromise<IPlannedRouteDetail> {
        const plannedRoutePromise = this
            .$http({
                url: `/api/tms/plannedroutes/${routeId}`,
                method: "GET"
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPlannedRoute>) => {
                return [response.data];
            });

        let unplannedStopsPromise = this.$q.resolve([] as IUnplannedStop[]);
        if (options.getUnplannedStops) {
            unplannedStopsPromise = this.unplannedStopService.getUnplannedStopsForRoute(routeId);
        }

        return this.getLinkedElementsForPlannedRoutes(plannedRoutePromise, unplannedStopsPromise, options).then(plannedRoutes => {
            return this.filterRoutes(plannedRoutes, options)[0];
        });
    }

    // Potential bug in here when used with route performance
    getPlannedRoutes(start: moment.Moment, end: moment.Moment, options: IPlannedRouteOptions, unsafe = false): angular.IPromise<IPlannedRouteDetail[]> {
        return this.cacheService.getOrRetrieveData(this.plannedRouteServiceCache, () => this.getPlannedRoutesFromDatabase(start, end, options), options.invalidateCache, unsafe);
    }

    getPlannedRoutesFromDatabase(start: moment.Moment, end: moment.Moment, options: IPlannedRouteOptions): angular.IPromise<IPlannedRouteDetail[]> {

        if (!options.customTimes) {
            // Ensure we are at the start and end of days
            start = start.startOf("d");
            end = end.endOf("d");
        }

        const plannedRoutePromise = this
            .$http({
                url: `/api/tms/plannedroutes?start=${start.toISOString()}&end=${end.toISOString()}`,
                method: "GET"
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPlannedRoute[]>) => {
                return response.data;
            });

        let unplannedStopsPromise = this.$q.resolve([] as IUnplannedStop[]);
        if (options.getUnplannedStops) {
            unplannedStopsPromise = this.unplannedStopService.getUnplannedStopsForRoutes(start, end, true);
        }

        return this.getLinkedElementsForPlannedRoutes(plannedRoutePromise, unplannedStopsPromise, options).then(plannedRoutes => {
            return this.filterRoutes(plannedRoutes, options);
        });
    }

    getLinkedElementsForPlannedRoutes(plannedRoutePromise: angular.IPromise<IPlannedRoute[]>, unplannedStopsPromise: angular.IPromise<IUnplannedStop[]>, options: IPlannedRouteOptions): angular.IPromise<IPlannedRouteDetail[]> {

        let vehiclesPromise = this.$q.resolve([] as IVehicle[]);
        if (options.mapVehicles) {
            vehiclesPromise = this.vehicleService.getVehicles({ includePeripheralMappings: true });
        }

        let driversPromise = this.$q.resolve([] as IDriver[]);
        if (options.mapDrivers) {
            driversPromise = this.driverService.getDrivers();
        }

        let assignmentsPromise = this.$q.resolve([] as IVehicleDriverMapping[]);
        if (options.mapVehicles && options.mapDrivers) {
            assignmentsPromise = this.driverAssignmentService.getDriverAssignments(true);
        }

        let geofencesPromise = this.$q.resolve([] as IGeofence[]);
        if (options.mapGeofences) {
            geofencesPromise = this.geofenceService.getGeofences();
        }

        return this.$q.all([plannedRoutePromise, unplannedStopsPromise, geofencesPromise, vehiclesPromise, driversPromise, assignmentsPromise])
            .then(
                ([plannedRoutes, unplannedStops, geofences, vehicles, drivers, assignments]) => {

                    const updatedPlannedRoutes: IPlannedRouteDetail[] = [];
                    const formatRoutePromises: angular.IPromise<void>[] = [];
                    
                    plannedRoutes.forEach(route => {
                        const updatedRoute: IPlannedRouteDetail = route;

                        updatedPlannedRoutes.push(updatedRoute);

                        if (!options.getCancelledVisits) {
                            this.filterOutCancelledVisits(route);
                        }

                        this.driverAssignmentService.mapDriversToVehicles(drivers, assignments, vehicles);
                        this.assignVehicleToRoute(updatedRoute, vehicles);
                        this.assignDriverToRoute(updatedRoute, drivers);
                        this.formatPlannedVisitsForDisplay(route, geofences);
                        this.formatRouteForDisplay(updatedRoute, geofences);
                        this.parseRouteRules(route);
                        this.groupPlannedAndUnplannedVisits(updatedRoute, unplannedStops, options);

                        formatRoutePromises.push(this.formatDistanceAndLineForRouteVisits(updatedRoute, options));
                    });

                    return this.$q.all(formatRoutePromises).then(() => {
                        return updatedPlannedRoutes;
                    });
                });
    }

    filterOutCancelledVisits(plannedRoute: IPlannedRoute): void {
        if (!plannedRoute?.PlannedVisits?.length) {
            return;
        }

        plannedRoute.PlannedVisits = plannedRoute.PlannedVisits.filter(visit => visit.Status !== RouteVisitStatus.Cancelled);
    }

    formatPlannedVisitsForDisplay(route: IPlannedRouteDetail, geofences: IGeofence[]) {
        route.PlannedVisits.forEach(visit => {
            this.plannedRouteVisitService.updateOnSiteTimeMins(visit);
            this.mapGeofenceToVisit(visit, geofences);
            this.setDisplayLabelForPlannedVisit(visit);
        });
    }


    groupPlannedAndUnplannedVisits(route: IPlannedRouteDetail, unplannedStops: IUnplannedStop[], options: IPlannedRouteOptions): void {
        route.unplannedVisits = unplannedStops;


        if (options.getUnplannedStops) {
            route.allVisits = [...this.createRouteDetailVisitFromPlannedVisits(route.PlannedVisits, options), ...this.createRouteDetailVisitFromUnplannedStops(route, options)];
        }
        else {
            route.allVisits = [...this.createRouteDetailVisitFromPlannedVisits(route.PlannedVisits, options)];
        }

        route.allVisits.sort((a, b) => {
            if (a.plannedArrivalTime < b.plannedArrivalTime) {
                return -1;
            }
            return 1;
        });
        route.allVisits.forEach((visit, index) => {
            visit.allVisitsId = index;
        });
    }

    formatRouteForDisplay(route: IPlannedRouteDetail, geofences: IGeofence[]): void {
        route.totalPlannedDistanceKm = 0;
        this.setEditableStatus(route);
        this.setDeletableStatus(route);
        this.setFirstAndLastVisit(route);
        this.mapGeofenceToVisit(route.firstVisit, geofences);
        this.mapGeofenceToVisit(route.lastVisit, geofences);
        this.setDisplayLabelForPlannedVisit(route.firstVisit);
        this.setDisplayLabelForPlannedVisit(route.lastVisit);
        this.setRouteDisplayName(route);
        this.setRouteDisplayDuration(route);
        this.setRouteDisplayPlannedDistance(route);
        this.setRouteDisplayStatus(route);
        this.setTotalPlannedVisits(route);
    }

    setEditableStatus(route: IPlannedRouteDetail) {
        route.editingDisabled = route.Status !== RouteStatus.Planned && route.Status !== RouteStatus.Started;
    }

    setDeletableStatus(route: IPlannedRouteDetail) {
        route.deletingDisabled = route.Status !== RouteStatus.Planned;
    }

    setRouteDisplayName(route: IPlannedRouteDetail): void {
        let name: string = route.ExternalReference ? route.ExternalReference : route.Name;
        if (!name) {
            const startLoc = route.firstVisit.displayLabel || route.firstVisit.Name || this.translationService.t("TmsHistory.UnknownLocation");
            const endLoc = route.lastVisit.displayLabel || route.lastVisit.Name || this.translationService.t("TmsHistory.UnknownLocation");
            name = `${startLoc} to ${endLoc}`;
        }
        route.routeDisplayName = name;
    }

    setRouteDisplayDuration(route: IPlannedRouteDetail): void {
        const durationMs = this.getRouteDuration(route);
        route.totalDurationMins = Math.abs(durationMs / 1000 / 60);
    }

    setRouteDisplayPlannedDistance(route: IPlannedRouteDetail): void {
        route.totalPlannedDistanceKm = this.plannedRouteVisitService.calculateTotalDistanceKm(route.PlannedVisits).planned;
    }

    getRouteDuration(route: IPlannedRouteDetail): number {
        // Only need to use planned visits to calculate duration as unplanned stops will not happen before leaving the starting location or after arriving at the end location
        if (route.Status === RouteStatus.Started) {
            return;
        }
        if (!route?.firstVisit) {
            return 0;
        }

        let endTime: Date;
        if (route.ActualEndTime) {
            endTime = route.ActualEndTime;
        }
        else if (route.lastVisit && !route.ActualEndTime && route.lastVisit.ActualArrivalTime) {
            endTime = route.lastVisit.ActualArrivalTime;
        }
        else if (route.lastVisit) {
            endTime = route.lastVisit.PlannedArrivalTime;
        }

        const startTime = route.firstVisit.ActualDepartureTime ? route.firstVisit.ActualDepartureTime : route.firstVisit.PlannedDepartureTime;

        const duration = new Date(endTime).getTime() - new Date(startTime).getTime();
        return duration;
    }

    sortRouteVisitsByPlannedArrivalTime(route: IPlannedRoute): void {
        this.sortVisitsByPlannedArrivalTime(route.PlannedVisits);
    }

    sortVisitsByPlannedArrivalTime(visits: IPlannedRouteVisit[]): void {
        if (!visits?.length) {
            return;
        }

        visits.sort((a, b) => {
            const timeA = a.PlannedArrivalTime ? new Date(a.PlannedArrivalTime) : null;
            const timeB = b.PlannedArrivalTime ? new Date(b.PlannedArrivalTime) : null;

            if (timeA < timeB) {
                return -1;
            }

            return 1;
        });
    }

    formatDistanceAndLineForRouteVisits(route: IPlannedRouteDetail, options: IPlannedRouteOptions): angular.IPromise<void> {

        const totalDistanceKm = this.plannedRouteVisitService.calculateTotalDistanceKm(route.PlannedVisits).planned;

        if ((!totalDistanceKm && options.updateDistance) || options.updateLinePoints) {
            return this.plannedRouteVisitService.updateDirectionsForVisits(route.PlannedVisits, options.updateDistance, options.updateLinePoints).then(visits => {
                route.PlannedVisits = visits;

                route.totalPlannedDistanceKm = this.plannedRouteVisitService.calculateTotalDistanceKm(route.PlannedVisits).planned;
            });
        }
        else {
            route.totalPlannedDistanceKm = totalDistanceKm;
        }
    }

    getRouteBetweenVisitLocations(startLocation, endLocation): angular.IPromise<google.maps.DirectionsRoute> {
        if (!startLocation || !endLocation) {
            return this.$q.resolve(null);
        }

        const latLngFrom = this.getLatLngsForVisit(startLocation);
        const latLngTo = this.getLatLngsForVisit(endLocation);

        if (!latLngFrom || !latLngTo) {
            return this.$q.resolve(null);
        }

        return this.googleService.getDirections(latLngFrom, latLngTo).then((response) => {
            return response.routes[0];
        });
    }

    getLatLngsForVisit(visit: IPlannedRouteVisit) {
        let latLng: google.maps.LatLng;

        if (visit.RouteStopLocation || visit.RouteStopLocationJson) {

            if (!visit.RouteStopLocation && visit.RouteStopLocationJson) {
                visit.RouteStopLocation = JSON.parse(visit.RouteStopLocationJson) as IGeoArea;
            }
            latLng = new google.maps.LatLng({
                lat: visit.RouteStopLocation.Latitude,
                lng: visit.RouteStopLocation.Longitude
            });
        }
        else if (visit.Geofence || visit.GeofenceId) {
            if (!visit.Geofence && visit.GeofenceId) {
                this.mapGeofenceToVisit(visit, this.geofences);
            }
            latLng = new google.maps.LatLng({
                lat: visit.Geofence.Latitude,
                lng: visit.Geofence.Longitude
            });
        }

        return latLng;
    }

    extractGeoAreasFromRoute(route: IPlannedRoute): IGeoArea[] {
        var geoAreas: IGeoArea[] = [];

        route.PlannedVisits.forEach(visit => {
            geoAreas.push(visit.RouteStopLocation);
        });

        return geoAreas;
    }

    assignVehicleToRoute(route: IPlannedRouteDetail, vehicles: IVehicle[]) {
        if (!route.VehicleId) {
            return;
        }
        const routeVehicle = vehicles.find(vehicle => {
            return vehicle.Id === route.VehicleId;
        });
        route.vehicle = routeVehicle;
    }

    assignDriverToRoute(route: IPlannedRouteDetail, drivers: IDriver[]) {
        if (!route.DriverId && !route.vehicle?.Driver) {
            return;
        }

        let routeDriver: IDriver = null;

        if (route.DriverId) {
            routeDriver = drivers.find(driver => {
                return driver.Id === route.DriverId;
            });
        }
        else {
            routeDriver = route.vehicle?.Driver;
        }

        route.driver = routeDriver;
    }

    filterRoutes(plannedRoutes: IPlannedRoute[], options: IPlannedRouteOptions): IPlannedRoute[] {
        const filteredPlannedRoutes: IPlannedRoute[] = [];

        plannedRoutes.forEach(route => {
            let addToList = true;
            if (route.Status === RouteStatus.Cancelled && !options.showCancelledRoutes) {
                addToList = false;
            }

            const routeCannotBeEdited = route.Status !== RouteStatus.Planned && route.Status !== RouteStatus.Started;

            if (routeCannotBeEdited && route.PlannedStartTime && options.onlyRoutesThatCanBeEdited) {
                addToList = false;
            }

            if (addToList) {
                filteredPlannedRoutes.push(route);
            }
        });

        return filteredPlannedRoutes;
    }

    setFirstAndLastVisit(route: IPlannedRouteDetail): void {
        if (!route.PlannedVisits?.length) {
            return;
        }

        route.firstVisit = route.PlannedVisits[0];
        route.lastVisit = route.PlannedVisits.slice(-1)[0];
    }

    mapGeofenceToVisit(visit: IPlannedRouteVisit, geofences: IGeofence[]) {
        if (!visit.GeofenceId) {
            return;
        }
        visit.Geofence = this.geofenceService.findGeofence(geofences, visit.GeofenceId);
    }

    setDisplayLabelForPlannedVisit(visit: IPlannedRouteVisit) {
        const {
            Name,
            GeocodedAddress,
            Geofence,
            RouteStopLocation,
            RouteStopLocationJson,
            Address
        } = visit;

        // Set displayLabel based on Name or fallback values
        visit.displayLabel = Name || "Unknown";

        if (!Name) {
            if (GeocodedAddress) {
                visit.displayLabel = visit.Name = GeocodedAddress.FormattedAddress;
            } else if (Geofence) {
                visit.displayLabel = visit.Name = Geofence.Description;
            } else if (RouteStopLocation) {
                visit.displayLabel = visit.Name = RouteStopLocation.Description;
            } else if (RouteStopLocationJson) {
                visit.RouteStopLocation = JSON.parse(RouteStopLocationJson) as IGeoArea;
                visit.displayLabel = visit.Name = visit.RouteStopLocation.Description;
            }
        }

        // Set displayNameAddressLabel based on address or geofence address fallback
        const address = Address || `${Geofence?.Addr1 ?? ''} ${Geofence?.Addr3 ?? ''}`.trim();

        visit.displayNameAddressLabel = address.length
            ? `${visit.displayLabel} (${address})`
            : visit.displayLabel;
    }

    setRouteDisplayStatus(route: IPlannedRouteDetail): void {
        switch (route.Status) {
            case RouteStatus.Planned:

                if (!route.firstVisit) {
                    return;
                }

                const ms = this.veriDate.createMoment(route.firstVisit.PlannedArrivalTime).diff(this.veriDate.createMoment(new Date()));
                const duration = this.veriDate.formatDurationMs(ms);

                if (ms.valueOf() > 0) {
                    route.routeDisplayStatus = `${this.translationService.t("Tms.StartsIn")} ${duration}`;
                    route.routeDetailDisplayStatus = "Planned";
                }
                else {
                    route.routeDisplayStatus = route.routeDetailDisplayStatus = this.translationService.t("Tms.Overdue");
                }

                break;

            case RouteStatus.Started:

                const visitCount = route.PlannedVisits.filter(visit => visit.Status == RouteVisitStatus.Arrived || visit.Status == RouteVisitStatus.Departed).length;

                route.routeDisplayStatus = `Visited ${visitCount} of ${route.PlannedVisits.length}`;
                route.routeDetailDisplayStatus = "In Progress"

                break;

            case RouteStatus.CompletedWithMissing:
            case RouteStatus.Completed:
                route.routeDisplayStatus = this.translationService.t("Tms.Ended");
                route.routeDetailDisplayStatus = this.translationService.t("Tms.Ended");

                break;

            default:
                route.routeDisplayStatus = RouteStatus[route.Status];
                break;
        }
    }

    setTotalPlannedVisits(route: IPlannedRouteDetail): void {
        let requiresStartLocation = false;
        let requiresEndLocation = false;

        if (route?.TmsRouteRules?.RouteMatchingRules) {
            requiresStartLocation = route.TmsRouteRules.RouteMatchingRules.StartRouteOnDepartStartingLocation;
            requiresEndLocation = route.TmsRouteRules.RouteMatchingRules.EndRouteOnArriveAtEndingLocation;
        }

        let totalPlannedVisits = route.PlannedVisits.length || 0;

        totalPlannedVisits = (requiresStartLocation) ? Math.max(0, totalPlannedVisits - 1) : totalPlannedVisits;
        totalPlannedVisits = (requiresEndLocation) ? Math.max(0, totalPlannedVisits - 1) : totalPlannedVisits;

        route.totalPlannedVisits = totalPlannedVisits;
    }

    openRouteDetailsDialogById(routeId: number, parentStateName = "app.tms.activeRoutes") {

        this.navigationService.go(`${parentStateName}.view`, { routeId: routeId });
    }

    openRouteDetailsDialog(route: IPlannedRouteDetail, parentStateName = "app.tms.activeRoutes") {

        this.openRouteDetailsDialogById(route.Id, parentStateName);
    }

    getArrivalVisitOnTimeStatus(visit: IPlannedRouteVisit): VisitOnTimeStatus {
        if (!visit.ActualArrivalTime) {
            // If we have no arrival time, but we do have a departure time, then this is our departure stop, so we shouldnt penalise for this
            if (visit.ActualDepartureTime) {
                return null;
            }

            return VisitOnTimeStatus.Missed;
        }

        const arrivalDiff = this.dateService.getDiff(visit.ActualArrivalTime, visit.PlannedArrivalTime, DateUnit.Min);
        const travelTimeBufferMins = this.veriDate.convertTimeSpanToMins(visit.TravelTimeBuffer);

        if (arrivalDiff > -travelTimeBufferMins && arrivalDiff < travelTimeBufferMins) {
            return VisitOnTimeStatus.OnTime;
        }

        if (arrivalDiff > -travelTimeBufferMins) {
            return VisitOnTimeStatus.Early;
        }

        if (arrivalDiff < travelTimeBufferMins) {
            return VisitOnTimeStatus.Late;
        }

        return VisitOnTimeStatus.OnTime;
    }

    getDepartureVisitOnTimeStatus(visit: IPlannedRouteVisit): VisitOnTimeStatus {
        if (!visit.ActualDepartureTime) {
            return null;
        }

        const departureDiff = this.veriDate.getDiff(visit.ActualDepartureTime, visit.PlannedDepartureTime, "m");
        const onSiteTimeBufferMins = this.veriDate.convertTimeSpanToMins(visit.OnSiteTimeBuffer);

        if (departureDiff > -onSiteTimeBufferMins && departureDiff < onSiteTimeBufferMins) {
            return VisitOnTimeStatus.OnTime;
        }

        if (departureDiff > -onSiteTimeBufferMins) {
            return VisitOnTimeStatus.Early;
        }

        if (departureDiff < onSiteTimeBufferMins) {
            return VisitOnTimeStatus.Late;
        }

        return VisitOnTimeStatus.OnTime;
    }

    getCostMultiplier(): number {
        const accountOptions = this.sessionService.getItem<AccountOption>(SettingsName.AccountOptions);
        let costMultiplier: number = parseFloat(accountOptions.JourneyCostValue);

        if (accountOptions.JourneyCostType === "Mileage") {
            // convert from cost per mile into cost per km
            costMultiplier /= 1.60934;
        }

        return costMultiplier;
    }


    // ---

    private createRouteDetailVisitFromPlannedVisits(visits: IPlannedRouteVisit[], options: IPlannedRouteOptions): IRouteDetailVisit[] {
        var routeDetails: IRouteDetailVisit[] = [];

        visits.forEach(visit => {
            var visitDetail = this.createRouteDetailVisitFromPlannedVisit(visit, options);

            visitDetail.arrivalTimeStatus = this.getArrivalVisitOnTimeStatus(visit);
            visitDetail.departureTimeStatus = this.getDepartureVisitOnTimeStatus(visit);

            routeDetails.push(visitDetail);
        });

        return routeDetails;
    }

    private createRouteDetailVisitFromPlannedVisit(visit: IPlannedRouteVisit, options: IPlannedRouteOptions): IRouteDetailVisit {
        if (options.mapGeofences && visit.RouteStopLocationJson) {
            visit.RouteStopLocation = JSON.parse(visit.RouteStopLocationJson) as IGeoArea;
        }

        if (options.mapGeofences && (visit.RouteStopLocation?.GeofenceType == GeofenceType.Polygon || (visit.RouteStopLocation?.Latitude == 0 && visit.RouteStopLocation?.Longitude == 0))) {
            this.geofenceService.setPolygonGeofenceVisualCenter(visit.RouteStopLocation);
        }

        if (options.mapGeofences && (!visit.RouteStopLocation && visit.Geofence)) {
            visit.RouteStopLocation = visit.Geofence;
        }

        return {
            displayName: visit.displayLabel,
            actualArrivalTime: visit.ActualArrivalTime ? new Date(visit.ActualArrivalTime) : null,
            plannedArrivalTime: visit.PlannedArrivalTime ? new Date(visit.PlannedArrivalTime) : null,
            actualDepartureTime: visit.ActualDepartureTime ? new Date(visit.ActualDepartureTime) : null,
            plannedDepartureTime: visit.PlannedDepartureTime ? new Date(visit.PlannedDepartureTime) : null,
            arrivalEta: visit.ArrivalEta ? visit.ArrivalEta : null,
            driverId: visit.ActualDriverId,
            vehicleId: visit.ActualVehicleId,
            visitType: VisitType.Planned,
            address: visit.Address,
            latitude: visit.RouteStopLocation?.Latitude,
            longitude: visit.RouteStopLocation?.Longitude,
            status: visit.Status,
            plotId: visit.ArrivalPlotId,
            plot: { Id: visit.ArrivalPlotId },
        } as IRouteDetailVisit;
    }

    private createRouteDetailVisitFromUnplannedStops(route: IPlannedRouteDetail, options: IPlannedRouteOptions): IRouteDetailVisit[] {
        const visits: IRouteDetailVisit[] = [];

        if (!route.unplannedVisits) {
            route.unplannedVisits = [];
        }

        route.unplannedVisits.forEach(stop => {
            visits.push(this.createRouteDetailVisitFromUnplannedStop(stop));
        });

        return visits;
    }

    private createRouteDetailVisitFromUnplannedStop(unplannedStop: IUnplannedStop): IRouteDetailVisit {

        const latitude = unplannedStop.Latitude;
        const longitude = unplannedStop.Longitude;

        return {
            actualArrivalTime: this.veriDate.createDate(unplannedStop.VisitTime),
            plannedArrivalTime: this.veriDate.createDate(unplannedStop.VisitTime),
            actualDepartureTime: unplannedStop.DepartureTime,
            plannedDepartureTime: unplannedStop.DepartureTime,
            driverId: unplannedStop.DriverId,
            vehicleId: unplannedStop.VehicleId,
            visitType: VisitType.Unplanned,
            status: RouteVisitStatus.Departed,
            plotId: unplannedStop.PlotId,
            plot: { Id: unplannedStop.PlotId, Latitude: latitude, Longitude: longitude },
            latitude: latitude,
            longitude: longitude
        };

    }



    private parseRouteRules(route: IPlannedRouteDetail): void {
        if (!route.TmsRouteRulesJson) {
            return;
        }

        route.TmsRouteRules = JSON.parse(route.TmsRouteRulesJson);
    }

    private removeDetailPropertiesForSave(plannedRoute: IPlannedRoute | IPlannedRouteDetail): void {
        delete (plannedRoute as IPlannedRouteDetail).driver;
        delete (plannedRoute as IPlannedRouteDetail).vehicle;
        delete (plannedRoute as IPlannedRouteDetail).firstVisit;
        delete (plannedRoute as IPlannedRouteDetail).lastVisit;
    }

}
